import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import userRoleService, { type RoleProfileDTO, type RoleActivationRequest } from '../services/userRoleService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  MenuItem,
  Chip,
  Stack,
  Collapse,
  IconButton,
} from '@mui/material';
import { FamilyRestroom, School, ExpandMore, ExpandLess } from '@mui/icons-material';

const SCHOOL_TYPES = ['Primary', 'Secondary'];

const UserRoleSections: React.FC = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<RoleProfileDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    userRoleService.getActivatedRoles()
      .then(data => setRoles(data.activatedRoles))
      .catch(err => setError(err.message || 'Failed to load roles'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <CircularProgress sx={{ display: 'block', mx: 'auto', my: 2 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  if (roles.length === 0) {
    return (
      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>No roles activated yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Activate a role to access your dashboard and personalised features.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/login')}>
            Activate a Role
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {roles.map(role => (
        <RoleCard key={role.roleType} role={role} />
      ))}
    </Box>
  );
};

const RoleCard: React.FC<{ role: RoleProfileDTO }> = ({ role }) => {
  const [expanded, setExpanded] = useState(true);
  const [form, setForm] = useState<RoleActivationRequest>({
    phoneNumber: role.phoneNumber || '',
    schoolCountry: role.schoolCountry || '',
    schoolCouncil: role.schoolCouncil || '',
    schoolName: role.schoolName || '',
    schoolType: role.schoolType || '',
    courseInterests: role.courseInterests || '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await userRoleService.updateRoleProfile(role.roleType, form);
      setSaveSuccess(true);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const isParent = role.roleType === 'PARENT';
  const isStudent = role.roleType === 'STUDENT';
  const RoleIcon = isParent ? FamilyRestroom : School;
  const roleLabel = role.roleType.charAt(0) + role.roleType.slice(1).toLowerCase();

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RoleIcon color="primary" />
            <Typography variant="h6">{roleLabel} Profile</Typography>
          </Box>
          <IconButton size="small" onClick={() => setExpanded(e => !e)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Saved successfully</Alert>}
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

          {isParent && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your contact details (phone number) are managed in the profile form above.
            </Typography>
          )}

          {isStudent && (
            <>
              <TextField
                fullWidth
                label="Country"
                value={form.schoolCountry || ''}
                onChange={e => setForm(f => ({ ...f, schoolCountry: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="School Council / Local Authority"
                value={form.schoolCouncil || ''}
                onChange={e => setForm(f => ({ ...f, schoolCouncil: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="School Name"
                value={form.schoolName || ''}
                onChange={e => setForm(f => ({ ...f, schoolName: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                select
                fullWidth
                label="School Type"
                value={form.schoolType || ''}
                onChange={e => setForm(f => ({ ...f, schoolType: e.target.value }))}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">— select —</MenuItem>
                {SCHOOL_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField
                fullWidth
                label="Phone Number (optional)"
                value={form.phoneNumber || ''}
                onChange={e => setForm(f => ({ ...f, phoneNumber: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Course Interests (optional)
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                  {(form.courseInterests || '').split(',').filter(Boolean).map(c => (
                    <Chip
                      key={c.trim()}
                      label={c.trim()}
                      onDelete={() => {
                        const updated = (form.courseInterests || '').split(',')
                          .filter(x => x.trim() !== c.trim()).join(',');
                        setForm(f => ({ ...f, courseInterests: updated }));
                      }}
                    />
                  ))}
                </Stack>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. Python, AWS (press Enter to add)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const val = (e.target as HTMLInputElement).value.trim();
                      if (val) {
                        const current = form.courseInterests || '';
                        const updated = current ? `${current},${val}` : val;
                        setForm(f => ({ ...f, courseInterests: updated }));
                        (e.target as HTMLInputElement).value = '';
                      }
                    }
                  }}
                />
              </Box>
            </>
          )}

          {!isParent && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              endIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default UserRoleSections;
